import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';
import { timeToMinutes, fmtClock, ymdLocal } from '../lib/format.js';
import { locationKeyFromName, isoMondayOf } from '../lib/schedule.js';
import { OpenShiftsForPickup } from './OpenShiftsForPickup.jsx';

// ─── My-only week view (compact, current user's shifts) ──────────────────────
export function MyOnlyWeekView({ location, user }) {
  const [weekStart, setWeekStart] = useState(() => isoMondayOf(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Bump this on pickup so useCallback re-creates load, which re-runs
  // the useEffect that fires it, which fetches fresh data — without
  // depending on closure / browser cache shenanigans.
  const [refreshTick, setRefreshTick] = useState(0);
  const locationKey = locationKeyFromName(location?.name);
  const mySquareId = user?.square_id || null;
  const myMonarchId = user?.id || null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Aggregated endpoint covers every location the user is authorized
      // at — so a barista who picks up a shift at one location while
      // viewing another's schedule still sees their pickup. Each shift
      // carries location_name for labelling.
      const res = await api('GET', `/api/me/schedule/week?week_start=${weekStart}&t=${Date.now()}`,
        null, { 'x-user-id': String(user.id) });
      setData(res); setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load my schedule');
    } finally { setLoading(false); }
  }, [weekStart, refreshTick]);
  useEffect(() => { load(); }, [load]);
  // onPickedUp from OpenShiftsForPickup goes through this so the
  // useCallback dep-change actually fires. Calling `load` directly
  // doesn't change deps.
  const refresh = useCallback(() => setRefreshTick(t => t + 1), []);

  const todayYmd = ymdLocal(new Date());
  const thisMon = isoMondayOf(new Date());
  function shiftWeek(weeks) {
    const d = new Date(weekStart + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + weeks * 7);
    setWeekStart(ymdLocal(d));
  }

  // Round 32: scroll today's day-card into view on first data load when
  // we're looking at the current week. Keeps the focus on what matters now.
  useEffect(() => {
    if (!data || weekStart !== thisMon) return;
    setTimeout(() => document.getElementById(`day-${todayYmd}`)?.scrollIntoView({ behavior: 'auto', block: 'start' }), 50);
  }, [data?.days?.length === 7]);

  // /api/me/schedule/week already filters to the calling user, so every
  // shift returned IS theirs. Just rename for the existing render code.
  const myDays = (data?.days || []).map((d) => ({
    ...d,
    myShifts: d.shifts || [],
  }));
  const totalMyShifts = myDays.reduce((s, d) => s + d.myShifts.length, 0);
  const totalHours = myDays.reduce((sum, d) =>
    sum + d.myShifts.reduce((s, sh) => {
      const a = timeToMinutes(sh.scheduledStart), b = timeToMinutes(sh.scheduledEnd);
      if (a == null || b == null) return s;
      return s + (b - a) / 60;
    }, 0), 0);

  return (
    <div className="space-y-3">
      {/* PunchBanner moved to Home → Daily in R44. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
        <button className="btn-secondary" style={{ padding: '6px 10px' }} onClick={() => shiftWeek(-1)}>← Prev</button>
        <button onClick={() => {
          setWeekStart(thisMon);
          setTimeout(() => document.getElementById(`day-${todayYmd}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
        }} style={{ padding: '6px 14px', fontSize: 12, fontWeight: 700,
                     background: 'var(--gold)', color: '#fff', border: 'none',
                     borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit' }}>
          Today
        </button>
        <button className="btn-secondary" style={{ padding: '6px 10px' }} onClick={() => shiftWeek(1)}>Next →</button>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginLeft: 4 }}>
          Wk of {new Date(weekStart + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </span>
        {data && (
          <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginLeft: 'auto' }}>
            {totalMyShifts} {totalMyShifts === 1 ? 'shift' : 'shifts'} · {totalHours.toFixed(1)} h
          </span>
        )}
      </div>

      {loading && !data && <div className="card text-sm text-gray-500">Loading my schedule…</div>}
      {error && <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
        <div className="text-sm font-semibold text-red-700">Schedule unavailable</div>
        <div className="text-xs text-gray-600 mt-1">{error}</div>
        <button className="btn-secondary mt-2" onClick={load}>Retry</button>
      </div>}

      {data && totalMyShifts === 0 && (
        <div className="card" style={{ borderLeft: '4px solid var(--gold, #c8a25b)', padding: 12, fontSize: 13 }}>
          You have nothing scheduled for this week. Use <strong>Next →</strong> to check upcoming weeks for picked-up or newly-published shifts.
        </div>
      )}
      {data && (
        <div className="week-grid-7" style={{ display: 'grid', gap: 6, gridTemplateColumns: 'repeat(7, minmax(0, 1fr))' }}>
          {myDays.map((d) => {
            const date = new Date(d.date + 'T12:00:00Z');
            const dayLabel = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][date.getUTCDay()];
            const friendly = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const isToday = d.date === todayYmd;
            const isPast = d.date < todayYmd;
            const isOff = d.myShifts.length === 0;
            const tagBg = isToday ? '#e8f5e9' : isPast ? '#f5f5f5' : '#e3f2fd';
            const tagText = isToday ? '#2e7d32' : isPast ? '#546e7a' : '#1565c0';
            return (
              <div key={d.date} id={`day-${d.date}`} className="card" style={{ padding: 8,
                                                          background: isOff ? '#fafafa' : '#fff',
                                                          opacity: isPast && isOff ? 0.55 : 1,
                                                          minWidth: 0, overflow: 'hidden',
                                                          scrollMarginTop: 80 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <div>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#212121' }}>{dayLabel}</span>
                    <span style={{ fontSize: 11, color: '#9e9e9e', marginLeft: 6 }}>{friendly}</span>
                  </div>
                  {isToday && (
                    <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 8,
                                   background: tagBg, color: tagText, textTransform: 'uppercase' }}>
                      Today
                    </span>
                  )}
                </div>
                {isOff ? (
                  <div style={{ fontSize: 12, color: '#9e9e9e', fontStyle: 'italic' }}>Off</div>
                ) : (
                  d.myShifts.map((s, i) => (
                    <div key={i} style={{ marginTop: i > 0 ? 6 : 0, padding: 6,
                                         border: '1px solid #e0e0e0', borderRadius: 6,
                                         background: '#fff' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#212121' }}>
                        {fmtClock(s.scheduledStart)}–{fmtClock(s.scheduledEnd)}
                      </div>
                      <div style={{ fontSize: 11, color: '#757575' }}>{s.location_name || location?.name || ''}</div>
                      {s.clockStatus === 'not_clocked_in' && (
                        <span style={{ fontSize: 9, fontWeight: 700, color: '#c62828', marginTop: 2, display: 'inline-block' }}>
                          ⚠ NO CLOCK
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            );
          })}
        </div>
      )}

      <OpenShiftsForPickup user={user} onPickedUp={refresh} />
    </div>
  );
}
